import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { HiExternalLink, HiX } from "react-icons/hi";
import { toast } from "react-toastify";
import {
  PaEmptyState,
  PaPageHeader,
  PaStatusBadge,
} from "components/platform-admin";
import FeatureOfTheWeekCard from "components/common/FeatureOfTheWeekCard/FeatureOfTheWeekCard";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import PaAccessDenied from "pages/PlatformAdmin/layout/PaAccessDenied";
import {
  paPrimaryButtonClass,
  paSecondaryButtonClass,
} from "pages/PlatformAdmin/platformAdminPage.utils";
import { platformRoleAccess } from "helpers/RolesPermission";
import {
  fetchFeatureOfTheWeek,
  fetchFeatureOfTheWeekVideoGallery,
  publishFeatureOfTheWeek,
  saveFeatureOfTheWeek,
} from "store/services/platformAdmin.services";
import {
  emptyFeatureOfTheWeekForm,
  FEATURE_OF_THE_WEEK_VIDEO_GALLERY_URL,
  findGalleryVideoByUrl,
  toVideoEmbedUrl,
} from "./featureOfTheWeek.utils";

const FeatureOfTheWeekAdmin = () => {
  const canManage = platformRoleAccess("platform.catalog.news");
  const [form, setForm] = useState(emptyFeatureOfTheWeekForm());
  const [galleryVideos, setGalleryVideos] = useState([]);
  const [galleryCategories, setGalleryCategories] = useState(["All"]);
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [gallerySearch, setGallerySearch] = useState("");
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [galleryError, setGalleryError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState(null);

  const loadFeature = useCallback(async () => {
    if (!canManage) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const feature = await fetchFeatureOfTheWeek();
      if (feature) {
        setForm({
          uuid: feature.uuid || null,
          title: feature.title || "Feature of the Week",
          description: feature.description || "",
          video_url: feature.video_url || "",
          status: feature.status || "draft",
        });
      } else {
        setForm(emptyFeatureOfTheWeekForm());
      }
    } catch (err) {
      setError(err?.message || "Failed to load Feature of the Week");
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  const loadVideoGallery = useCallback(async () => {
    if (!canManage) {
      setGalleryLoading(false);
      return;
    }

    setGalleryLoading(true);
    setGalleryError(null);

    try {
      const gallery = await fetchFeatureOfTheWeekVideoGallery();
      setGalleryVideos(Array.isArray(gallery?.videos) ? gallery.videos : []);
      setGalleryCategories(
        Array.isArray(gallery?.categories) && gallery.categories.length
          ? gallery.categories
          : ["All"]
      );
    } catch (err) {
      setGalleryVideos([]);
      setGalleryError(err?.message || "Failed to load Video Gallery from vertex360.io");
    } finally {
      setGalleryLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    loadFeature();
    loadVideoGallery();
  }, [loadFeature, loadVideoGallery]);

  const filteredVideos = useMemo(() => {
    const search = gallerySearch.trim().toLowerCase();

    return galleryVideos.filter((video) => {
      const matchesCategory =
        categoryFilter === "All" || video.category === categoryFilter;
      const matchesSearch =
        !search ||
        video.title?.toLowerCase().includes(search) ||
        video.category?.toLowerCase().includes(search);

      return matchesCategory && matchesSearch;
    });
  }, [galleryVideos, categoryFilter, gallerySearch]);

  const selectedGalleryVideo = useMemo(
    () => findGalleryVideoByUrl(galleryVideos, form.video_url),
    [galleryVideos, form.video_url]
  );

  const previewEmbedUrl = useMemo(
    () =>
      selectedGalleryVideo?.embed_url || toVideoEmbedUrl(form.video_url),
    [selectedGalleryVideo, form.video_url]
  );

  const canPreview = Boolean(
    form.description.trim() || form.video_url.trim()
  );

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSelectGalleryVideo = (event) => {
    const videoUrl = event.target.value;
    if (!videoUrl) {
      setForm((prev) => ({ ...prev, video_url: "" }));
      return;
    }

    const video = findGalleryVideoByUrl(galleryVideos, videoUrl);
    setForm((prev) => ({
      ...prev,
      video_url: video?.video_url || videoUrl,
      title: video?.title || prev.title || "Feature of the Week",
    }));
  };

  const handleSaveDraft = async () => {
    if (!form.description.trim() || !form.video_url.trim()) {
      toast.error("Description and a Video Gallery video are required");
      return;
    }

    setSaving(true);
    try {
      const feature = await saveFeatureOfTheWeek({
        ...form,
        status: "draft",
      });
      setForm({
        uuid: feature.uuid,
        title: feature.title || "Feature of the Week",
        description: feature.description || "",
        video_url: feature.video_url || "",
        status: feature.status || "draft",
      });
      toast.success("Draft saved");
    } catch (err) {
      toast.error(err?.message || "Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!form.description.trim() || !form.video_url.trim()) {
      toast.error(
        "Description and a Video Gallery video are required before publishing"
      );
      return;
    }

    setPublishing(true);
    try {
      let feature = await saveFeatureOfTheWeek({
        ...form,
        status: "published",
      });

      if (feature.status !== "published" && feature.uuid) {
        feature = await publishFeatureOfTheWeek(feature.uuid);
      }

      setForm({
        uuid: feature.uuid,
        title: feature.title || "Feature of the Week",
        description: feature.description || "",
        video_url: feature.video_url || "",
        status: feature.status || "published",
      });
      toast.success("Feature of the Week published to the live dashboard");
    } catch (err) {
      toast.error(err?.message || "Failed to publish");
    } finally {
      setPublishing(false);
    }
  };

  if (!canManage) {
    return (
      <PaAccessDenied
        title="Feature of the Week"
        description="Only Super Admin / Catalog staff with platform news permission can manage Feature of the Week."
      />
    );
  }

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title="Feature of the Week"
        subtitle="Select a video from the Vertex360 Video Gallery, add a short description, preview, then publish to System News."
        actions={
          <a
            href={FEATURE_OF_THE_WEEK_VIDEO_GALLERY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`${paSecondaryButtonClass} inline-flex items-center gap-2`}
          >
            Open Video Gallery
            <HiExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        }
      />

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-sm text-gray-500">
          Loading Feature of the Week…
        </div>
      ) : error ? (
        <PaEmptyState
          title="Unable to load"
          description={error}
          action={
            <button type="button" className={paPrimaryButtonClass} onClick={loadFeature}>
              Retry
            </button>
          }
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-gray-900">Content</h2>
              <PaStatusBadge
                label={form.status === "published" ? "Published" : "Draft"}
                variant={form.status === "published" ? "success" : "neutral"}
              />
            </div>

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="fotw-gallery-video"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Video from Video Gallery
                </label>
                <div className="mb-2 grid gap-2 sm:grid-cols-2">
                  <select
                    value={categoryFilter}
                    onChange={(event) => setCategoryFilter(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {galleryCategories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  <input
                    type="search"
                    value={gallerySearch}
                    onChange={(event) => setGallerySearch(event.target.value)}
                    placeholder="Search gallery videos"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <select
                  id="fotw-gallery-video"
                  value={selectedGalleryVideo?.video_url || ""}
                  onChange={handleSelectGalleryVideo}
                  disabled={galleryLoading || Boolean(galleryError)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">
                    {galleryLoading
                      ? "Loading videos from Video Gallery…"
                      : "Select a video from https://vertex360.io/video-gallery/"}
                  </option>
                  {filteredVideos.map((video) => (
                    <option key={`${video.category}-${video.video_url}`} value={video.video_url}>
                      {video.category} — {video.title}
                      {video.duration ? ` (${video.duration})` : ""}
                    </option>
                  ))}
                </select>
                {galleryError ? (
                  <>
                    <p className="mt-1 text-xs text-red-600">
                      {galleryError}{" "}
                      <button
                        type="button"
                        className="underline"
                        onClick={loadVideoGallery}
                      >
                        Retry gallery
                      </button>
                    </p>
                    <input
                      type="url"
                      value={form.video_url}
                      onChange={handleChange("video_url")}
                      placeholder="https://www.youtube.com/watch?v=… or https://www.loom.com/share/…"
                      className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Use a share link from{" "}
                      <a
                        href={FEATURE_OF_THE_WEEK_VIDEO_GALLERY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 underline"
                      >
                        vertex360.io/video-gallery
                      </a>{" "}
                      when the gallery list cannot load.
                    </p>
                  </>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    Videos are loaded live from{" "}
                    <a
                      href={FEATURE_OF_THE_WEEK_VIDEO_GALLERY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 underline"
                    >
                      vertex360.io/video-gallery
                    </a>
                    {galleryVideos.length
                      ? ` (${galleryVideos.length} available).`
                      : "."}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="fotw-title"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Title
                </label>
                <input
                  id="fotw-title"
                  type="text"
                  value={form.title}
                  onChange={handleChange("title")}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Feature of the Week"
                />
              </div>

              <div>
                <label
                  htmlFor="fotw-description"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Short description
                </label>
                <textarea
                  id="fotw-description"
                  rows={5}
                  value={form.description}
                  onChange={handleChange("description")}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Briefly describe this week's feature for dashboard users"
                />
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  type="button"
                  className={paSecondaryButtonClass}
                  disabled={!canPreview}
                  onClick={() => setPreviewOpen(true)}
                >
                  Preview
                </button>
                <button
                  type="button"
                  className={paSecondaryButtonClass}
                  disabled={saving || publishing}
                  onClick={handleSaveDraft}
                >
                  {saving ? "Saving…" : "Save draft"}
                </button>
                <button
                  type="button"
                  className={paPrimaryButtonClass}
                  disabled={saving || publishing}
                  onClick={handlePublish}
                >
                  {publishing ? "Publishing…" : "Publish live"}
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-sm font-semibold text-gray-900">
              Live card preview
            </h2>
            <FeatureOfTheWeekCard
              title={form.title}
              description={form.description}
              videoUrl={form.video_url}
              embedUrl={previewEmbedUrl}
              emptyMessage="Select a Video Gallery video and add a description to preview the card."
            />
          </div>
        </div>
      )}

      <Transition.Root show={previewOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[10002]"
          onClose={() => setPreviewOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/50" />
          </Transition.Child>

          <div className="fixed inset-0 z-[10002] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-4"
              >
                <Dialog.Panel className="w-full max-w-xl rounded-xl bg-white p-6 shadow-xl">
                  <div className="mb-4 flex items-start justify-between gap-4">
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        Preview
                      </Dialog.Title>
                      <Dialog.Description className="mt-1 text-sm text-gray-500">
                        This is how the card will appear in System News after
                        publishing.
                      </Dialog.Description>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPreviewOpen(false)}
                      className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                      <HiX className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>

                  <FeatureOfTheWeekCard
                    title={form.title}
                    description={form.description}
                    videoUrl={form.video_url}
                    embedUrl={previewEmbedUrl}
                    emptyMessage="Nothing to preview yet."
                  />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </PlatformAdminLayout>
  );
};

export default FeatureOfTheWeekAdmin;
